import { useMemo } from 'react';
import { loadState, formatUSD } from '../lib/storage';
import { TrendingUp, TrendingDown, Wallet, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const PIE_COLORS = ['#1D4ED8', '#E11D48', '#F59E0B', '#10B981', '#8B5CF6', '#0EA5E9', '#EC4899', '#84CC16'];

export default function Dashboard() {
  const state = useMemo(() => loadState(), []);
  const { categories, transactions, accounts, monthlyBudget } = state;

  const totalIncome = transactions.filter((t) => t.type === 'income').reduce((a, t) => a + t.amount, 0);
  const totalExpense = transactions.filter((t) => t.type === 'expense').reduce((a, t) => a + Math.abs(t.amount), 0);
  const netWorth = accounts.reduce((a, x) => a + x.balance, 0);
  const budgetSpent = categories.reduce((a, c) => a + Number(c.spent || 0), 0);
  const budgetTotal = monthlyBudget || categories.reduce((a, c) => a + Number(c.budget || 0), 0);
  const pctUsed = Math.min(100, Math.round((budgetSpent / Math.max(1, budgetTotal)) * 100));

  const chartData = categories
    .filter((c) => Number(c.spent) > 0)
    .sort((a, b) => b.spent - a.spent)
    .slice(0, 8)
    .map((c) => ({ name: c.name.replace(' Bill', '').replace(' Projects', ''), spent: c.spent, budget: c.budget }));

  const pieData = categories
    .filter((c) => Number(c.spent) > 0)
    .sort((a, b) => b.spent - a.spent)
    .slice(0, 6)
    .map((c) => ({ name: c.name, value: c.spent }));

  const summaryCards = [
    { label: 'Total Net Worth', value: formatUSD(netWorth), icon: Wallet, tone: 'blue', hint: 'Across all accounts' },
    { label: 'Income (Feb)', value: formatUSD(totalIncome), icon: ArrowUpRight, tone: 'emerald', hint: 'Tithes & offerings' },
    { label: 'Expenses (Feb)', value: formatUSD(totalExpense), icon: ArrowDownRight, tone: 'red', hint: 'Bills & ministry' },
    {
      label: 'Budget Used',
      value: `${pctUsed}%`,
      icon: pctUsed > 80 ? TrendingDown : TrendingUp,
      tone: pctUsed > 80 ? 'red' : 'blue',
      hint: `${formatUSD(budgetSpent)} of ${formatUSD(budgetTotal)}`,
    },
  ];

  const recent = [...transactions].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 6);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--gcbc-red-deep)] font-bold">Home / Dashboard</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1">February overview</h1>
          <p className="text-sm text-slate-600 mt-1">Where the church is financially today.</p>
        </div>
        <div className="rounded-full px-4 py-2 bg-white border border-slate-200 text-xs font-semibold text-slate-700 shadow-sm">
          Reporting month: <span className="text-[var(--gcbc-royal-deep)]">February 2026</span>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-summary-cards">
        {summaryCards.map((c, i) => (
          <div
            key={c.label}
            data-testid={`summary-card-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
            className="gcbc-accent-top gcbc-rise rounded-2xl bg-white border border-slate-200 p-5 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-transform"
            style={{ animationDelay: `${i * 80}ms` }}
          >
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{c.label}</div>
              <div
                className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                  c.tone === 'red' ? 'bg-red-50 text-[var(--gcbc-red-deep)]' :
                  c.tone === 'emerald' ? 'bg-emerald-50 text-emerald-700' :
                  'bg-blue-50 text-[var(--gcbc-royal-deep)]'
                }`}
              >
                <c.icon className="w-4 h-4" />
              </div>
            </div>
            <div className="font-display text-3xl font-black text-slate-900 mt-3">{c.value}</div>
            <div className="text-xs text-slate-500 mt-1">{c.hint}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 gcbc-accent-top rounded-2xl bg-white border border-slate-200 shadow-sm p-5" data-testid="chart-spending-by-category">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-lg font-bold text-slate-900">Spending by category</h3>
              <p className="text-xs text-slate-500">Top 8 categories · budget vs spent</p>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#475569' }} interval={0} angle={-18} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11, fill: '#475569' }} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => formatUSD(v)} contentStyle={{ borderRadius: 8, borderColor: '#CBD5E1' }} />
                <Bar dataKey="budget" fill="#93C5FD" radius={[6, 6, 0, 0]} name="Budget" />
                <Bar dataKey="spent" fill="#1D4ED8" radius={[6, 6, 0, 0]} name="Spent" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="gcbc-accent-top rounded-2xl bg-white border border-slate-200 shadow-sm p-5" data-testid="chart-spending-mix">
          <h3 className="font-display text-lg font-bold text-slate-900">Where the money goes</h3>
          <p className="text-xs text-slate-500 mb-2">Top spending mix</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={85} paddingAngle={2}>
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatUSD(v)} contentStyle={{ borderRadius: 8, borderColor: '#CBD5E1' }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent transactions — bootstrap-style table */}
      <div className="gcbc-accent-top rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="recent-transactions-panel">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900">Recent transactions</h3>
            <p className="text-xs text-slate-500">Last {recent.length} entries</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="table gcbc-table mb-0 align-middle">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Category</th>
                <th className="text-end">Amount</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((t) => (
                <tr key={t.id} data-testid={`transaction-row-${t.id}`}>
                  <td className="text-slate-600 text-sm">{t.date}</td>
                  <td className="text-slate-900 font-medium">{t.description}</td>
                  <td>
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                        t.type === 'income'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-blue-50 text-[var(--gcbc-royal-deep)]'
                      }`}
                    >
                      {t.category}
                    </span>
                  </td>
                  <td className={`text-end font-bold ${t.type === 'income' ? 'text-emerald-700' : 'text-[var(--gcbc-red-deep)]'}`}>
                    {t.type === 'income' ? '+' : '−'} {formatUSD(Math.abs(t.amount))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
